// ---------------------------------------------------------------------------
// Installation prompt (PLAN 6).
//
// iOS has no install prompt at all -- Share -> Add to Home Screen is the only
// path, so it gets written instructions. Android fires beforeinstallprompt,
// which we capture and re-fire from a button.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
}

function isIos(): boolean {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // iPadOS 13+ reports as a Mac; the touch point count gives it away.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Non-standard iOS Safari flag.
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function InstallBanner() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  if (dismissed || isStandalone()) return null;

  if (deferred) {
    return (
      <aside className="install">
        <p>Install YuCanvas for offline use and a bigger map.</p>
        <div className="install__actions">
          <button
            className="btn btn--secondary"
            onClick={() => {
              void deferred.prompt();
              setDeferred(null);
            }}
          >
            Install
          </button>
          <button className="btn btn--link" onClick={() => setDismissed(true)}>
            Not now
          </button>
        </div>
      </aside>
    );
  }

  if (!isIos()) return null;

  return (
    <aside className="install">
      <p>
        <strong>Add this to your home screen.</strong> Tap the Share button
        (the square with an arrow), scroll down, then tap{' '}
        <strong>Add to Home Screen</strong>. Installed, the app keeps your data
        far more reliably than a Safari tab.
      </p>
      <button className="btn btn--link" onClick={() => setDismissed(true)}>
        Dismiss
      </button>
    </aside>
  );
}
